import type { BootServices } from '../../bootstrap';
import { getClientSessionId } from '../../utils/clientSession';
import { reportHandledError } from '../../monitoring/uiErrorSink';
import { apiFetch } from '../../utils/api';

export type DeductResult =
  | { ok: true; newBalance: number | null; runId?: string | null }
  | {
      ok: false;
      reason: 'auth' | 'insufficient' | 'premium_required' | 'private_show_required' | 'network';
      balance?: number;
      required?: number;
      runId?: string | null;
      private_show?: any;
    };

export type ExecuteCommandResult =
  | {
      ok: true;
      runId?: string | null;
      charged: boolean;
      already_owned: boolean;
      commandKey: string;
      listed_cost: number;
      spent_cost: number;
      balance: number | null;
      deduped?: boolean;
      included?: boolean;
      access_source?: string | null;
      private_show_grant_id?: string | null;
    }
  | {
      ok: false;
      reason: 'auth' | 'insufficient' | 'premium_required' | 'private_show_required' | 'network';
      balance?: number;
      required?: number;
      runId?: string | null;
      commandKey?: string;
      private_show?: any;
    };

/**
 * Server-authoritative command execution (cross-device entitlements).
 * The server:
 * - resolves cost/premiumOnly from scenario (modelId+kind+stateId+commandName)
 * - checks entitlement
 * - charges only if needed
 * - starts model-chat run and returns runId
 */
export async function executePlayerCommand(opts: {
  services: BootServices;
  modelId: number;
  kind: 'transition' | 'once';
  stateId: string;
  commandName: string;
  displayText?: string;
  uiLocale?: string;
  chatSessionId: string;
  commandEventId?: string;
}): Promise<ExecuteCommandResult> {
  const { services, modelId, kind, stateId, commandName, displayText, uiLocale, chatSessionId, commandEventId } = opts;
  const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
  if (!hasJwt) return { ok: false, reason: 'auth' };
  const accessToken = services.supabase.getAccessToken();
  if (!accessToken) return { ok: false, reason: 'auth' };

  const apiBaseUrl = services.config.config.apiBaseUrl;
  const eventId =
    (typeof commandEventId === 'string' && commandEventId.trim())
      ? commandEventId.trim()
      : (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : null);

  try {
    const res = await apiFetch(`${apiBaseUrl}/api/player/command/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        modelId,
        kind,
        stateId,
        commandName,
        chatSessionId,
        commandEventId: eventId,
        displayText: displayText || null,
        uiLocale: uiLocale || null,
        clientSessionId: getClientSessionId(),
      }),
    });
    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const code = String(data?.code || data?.error || '').toUpperCase();
      const runId = data?.runId ? String(data.runId) : null;
      const commandKey = data?.commandKey ? String(data.commandKey) : undefined;
      if (res.status === 403 && (code.includes('PREMIUM_REQUIRED') || String(data?.error || '').toLowerCase().includes('premium'))) {
        return { ok: false, reason: 'premium_required', runId, commandKey };
      }
      const errorText = String(data?.error || '').toLowerCase();
      if (res.status === 402 && errorText === 'private_show_required') {
        return {
          ok: false,
          reason: 'private_show_required',
          required: Number(data?.required ?? data?.required_credits ?? 0),
          runId,
          commandKey,
          private_show: data?.private_show || null,
        };
      }
      if (res.status === 402 && (errorText.includes('insufficient') || errorText.includes('premium_limit_exhausted'))) {
        return {
          ok: false,
          reason: 'insufficient',
          balance: Number(data?.balance ?? 0),
          required: Number(data?.required ?? 0),
          runId,
          commandKey,
        };
      }
      // Unexpected server rejection: report (handled; no UI).
      try {
        reportHandledError({
          err: new Error(`http_${res.status}`),
          kind: 'api',
          code: 'chat.command_execute_request_failed',
          userMessage: '',
          severity: 'error',
          context: {
            feature: 'chat',
            endpoint: '/api/player/command/execute',
            stage: 'http_not_ok',
            status: res.status,
            modelId,
            kind,
            stateId,
            commandName,
            commandKey: commandKey || `${kind}:${commandName}`,
            chatSessionId,
            resp: data || null,
          },
        });
      } catch {}
      return { ok: false, reason: 'network', runId, commandKey };
    }

    return {
      ok: true,
      runId: data?.runId ? String(data.runId) : null,
      charged: Boolean(data?.charged),
      already_owned: Boolean(data?.already_owned),
      commandKey: String(data?.commandKey || `${kind}:${commandName}`),
      listed_cost: Number(data?.listed_cost ?? 0) || 0,
      spent_cost: Number(data?.spent_cost ?? 0) || 0,
      balance: (data?.balance === null || data?.balance === undefined) ? null : Number(data.balance),
      deduped: Boolean(data?.deduped),
      included: data?.included === true,
      access_source: data?.access_source ? String(data.access_source) : null,
      private_show_grant_id: data?.private_show_grant_id ? String(data.private_show_grant_id) : null,
    };
  } catch (e) {
    // Network/transport failure: report (handled; no UI).
    try {
      reportHandledError({
        err: e,
        kind: 'transport',
        code: 'chat.command_execute_request_failed',
        userMessage: '',
        severity: 'error',
        context: {
          feature: 'chat',
          endpoint: '/api/player/command/execute',
          stage: 'fetch_exception',
          modelId,
          kind,
          stateId,
          commandName,
          commandKey: `${kind}:${commandName}`,
          chatSessionId,
        },
      });
    } catch {}
    return { ok: false, reason: 'network' };
  }
}

export async function listPlayerEntitlements(opts: {
  services: BootServices;
  modelId: number;
}): Promise<{ ok: true; items: string[] } | { ok: false; reason: 'auth' | 'network' }> {
  const { services, modelId } = opts;
  const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
  if (!hasJwt) return { ok: false, reason: 'auth' };
  const accessToken = services.supabase.getAccessToken();
  if (!accessToken) return { ok: false, reason: 'auth' };
  const apiBaseUrl = services.config.config.apiBaseUrl;
  try {
    const res = await apiFetch(`${apiBaseUrl}/api/player/entitlements?modelId=${encodeURIComponent(String(modelId))}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      try {
        reportHandledError({
          err: new Error(`http_${res.status}`),
          kind: 'api',
          code: 'chat.entitlements_fetch_failed',
          userMessage: '',
          severity: 'warning',
          context: {
            feature: 'chat',
            endpoint: '/api/player/entitlements',
            stage: 'http_not_ok',
            status: res.status,
            modelId,
            resp: data || null,
          },
        });
      } catch {}
      return { ok: false, reason: 'network' };
    }
    if (!res.ok || !data?.ok) return { ok: false, reason: 'network' };
    const items = Array.isArray(data?.items) ? data.items.map((x: any) => String(x || '').trim()).filter(Boolean) : [];
    return { ok: true, items };
  } catch (e) {
    try {
      reportHandledError({
        err: e,
        kind: 'transport',
        code: 'chat.entitlements_fetch_failed',
        userMessage: '',
        severity: 'warning',
        context: {
          feature: 'chat',
          endpoint: '/api/player/entitlements',
          stage: 'fetch_exception',
          modelId,
        },
      });
    } catch {}
    return { ok: false, reason: 'network' };
  }
}

/**
 * Записывает использование ЛЮБОЙ команды (платной или бесплатной)
 * Создаёт сессию если её ещё нет
 */
export async function recordCommand(opts: {
  services: BootServices;
  commandName: string;
  displayText?: string;
  uiLocale?: string;
  modelId: number;
  chatSessionId?: string;
  creditsSpent?: number;
  commandEventId?: string;
  premiumOnly?: boolean;
}): Promise<
  | { ok: true; sessionId?: string; runId?: string | null }
  | { ok: false; reason?: 'premium_required' | 'private_show_required' | 'auth' | 'network'; runId?: string | null; required?: number; private_show?: any }
> {
  const { services, commandName, displayText, uiLocale, modelId, chatSessionId, creditsSpent = 0, commandEventId, premiumOnly } = opts;

  const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
  if (!hasJwt) return { ok: false };

  const accessToken = services.supabase.getAccessToken();
  if (!accessToken) return { ok: false };

  const apiBaseUrl = services.config.config.apiBaseUrl;
  try {
    const eventId =
      (typeof commandEventId === 'string' && commandEventId.trim())
        ? commandEventId.trim()
        : (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : null);
    const res = await apiFetch(`${apiBaseUrl}/api/session/command`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        commandName,
        displayText: displayText || null,
        uiLocale: uiLocale || null,
        modelId,
        chatSessionId: chatSessionId || null,
        creditsSpent,
        commandEventId: eventId,
        clientSessionId: getClientSessionId(),
        premiumOnly: premiumOnly === true,
      }),
    });
    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const code = String(data?.code || data?.error || '').toUpperCase();
      const runId = data?.runId ? String(data.runId) : null;
      if (code.includes('PREMIUM_REQUIRED') || String(data?.error || '').toLowerCase().includes('premium')) {
        return { ok: false, reason: 'premium_required', runId };
      }
      if (res.status === 402 && String(data?.error || '').toLowerCase() === 'private_show_required') {
        return { ok: false, reason: 'private_show_required', runId, private_show: data?.private_show || null, required: Number(data?.required ?? data?.required_credits ?? 0) };
      }
      console.warn('[recordCommand] Failed:', data?.error || data?.message || res.status);
      return { ok: false, reason: 'network', runId };
    }

    return { ok: true, sessionId: data?.sessionId, runId: data?.runId ? String(data.runId) : null };
  } catch (e) {
    console.warn('[recordCommand] Network error:', e);
    return { ok: false, reason: 'network' };
  }
}

export async function deductCredits(opts: {
  services: BootServices;
  amount: number;
  commandName: string;
  displayText?: string;
  uiLocale?: string;
  modelId: number;
  chatSessionId?: string;
  commandEventId?: string;
  premiumOnly?: boolean;
}): Promise<DeductResult> {
  const { services, amount, commandName, displayText, uiLocale, modelId, chatSessionId, commandEventId, premiumOnly } = opts;
  if (!amount || amount <= 0) return { ok: true, newBalance: null };

  const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
  if (!hasJwt) return { ok: false, reason: 'auth' };

  const accessToken = services.supabase.getAccessToken();
  if (!accessToken) return { ok: false, reason: 'auth' };

  const apiBaseUrl = services.config.config.apiBaseUrl;
  try {
    const res = await apiFetch(`${apiBaseUrl}/api/credits/deduct`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        amount,
        commandName,
        displayText: displayText || null,
        uiLocale: uiLocale || null,
        modelId,
        chatSessionId: chatSessionId || null,
        commandEventId,
        clientSessionId: getClientSessionId(),
        premiumOnly: premiumOnly || false,
      }),
    });
    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const code = String(data?.code || '').toUpperCase();
      if (res.status === 403 && (code === 'PREMIUM_REQUIRED' || String(data?.error || '').toLowerCase().includes('premium'))) {
        return { ok: false, reason: 'premium_required', runId: data?.runId ? String(data.runId) : null };
      }
      if (res.status === 402 && String(data?.error || '').toLowerCase() === 'private_show_required') {
        return {
          ok: false,
          reason: 'private_show_required',
          runId: data?.runId ? String(data.runId) : null,
          private_show: data?.private_show || null,
          required: Number(data?.required ?? data?.required_credits ?? 0),
        };
      }
      if (String(data?.error || '').toLowerCase().includes('insufficient')) {
        return { ok: false, reason: 'insufficient', balance: Number(data?.balance ?? 0), runId: data?.runId ? String(data.runId) : null };
      }
      return { ok: false, reason: 'network', runId: data?.runId ? String(data.runId) : null };
    }

    return { ok: true, newBalance: Number(data?.balance ?? 0), runId: data?.runId ? String(data.runId) : null };
  } catch {
    return { ok: false, reason: 'network' };
  }
}
